import { TERMS_DATE, TERMS_VERSION } from '../config/legalDocuments';
import type { LegalDocument } from './types';

/**
 * Melio Membership Terms of Service — Patient Facing.
 *
 * Ported VERBATIM from the controlled document "Melio_Membership_Terms_v1.3.md"
 * (Melio Health Solutions Ltd, 11 September 2026). Do not paraphrase, tidy or
 * add to the wording; if this file and the controlled document disagree, the
 * document wins and this file is wrong. Update TERMS_VERSION / TERMS_DATE in
 * config/legalDocuments alongside any change, and consider whether
 * ONBOARDING_CONSENT_VERSION (types/consent) needs bumping.
 *
 * Contact addresses are the document's own: help@meliotherapeutics.uk.
 */
export const TERMS_DOCUMENT: LegalDocument = {
  id: 'terms',
  kicker: 'Terms',
  title: 'Melio Membership Terms of Service',
  pageTitle: 'Membership terms · Melio',
  description:
    "The terms that govern Melio's website, apps, member portal and preventive health service.",
  version: TERMS_VERSION,
  updated: TERMS_DATE,
  intro: [],
  sections: [
    {
      number: 1,
      title: 'About These Terms',
      blocks: [
        { type: 'p', text: "These terms govern your use of Melio's website, apps, member portal and preventive health service." },
        { type: 'p', text: 'When we refer to:' },
        {
          type: 'ul',
          items: [
            '"Melio", "we", "our", or "us", we mean Melio Health Solutions Ltd',
            '"you", "your", or "member", we mean the individual purchasing or using the service',
            '"Service", we mean Melio\'s preventive assessment, testing coordination, care planning and related digital tools',
          ],
        },
        { type: 'p', text: 'By using the Service, you agree to these Terms.' },
      ],
    },
    {
      number: 2,
      title: 'What Melio Provides',
      blocks: [
        { type: 'p', text: 'Melio combines health and family history, genetic and blood-test results, and clinician review into a care and monitoring plan.' },
        { type: 'p', text: 'Depending on the package you purchase, the Service may include:' },
        {
          type: 'ul',
          items: [
            'account creation and onboarding',
            'collection of health and family-history information',
            'genetic and blood testing through external partners',
            'clinician review',
            'a care plan and follow-up recommendations',
            "messaging, reminders, and administrative support through Melio's platform",
          ],
        },
        { type: 'p', text: 'The Service does not replace emergency, primary, specialist or inpatient care.' },
      ],
    },
    {
      number: 3,
      title: 'Important Service Limits',
      blocks: [
        { type: 'p', text: 'Melio is not an emergency service.' },
        {
          type: 'p',
          text: 'You must not rely on Melio for urgent or emergency concerns. If you have chest pain, shortness of breath, symptoms of stroke, heavy bleeding, a new breast lump, severe neurological symptoms, thoughts of self-harm, or any other urgent concern, you should contact emergency services, NHS 111, your GP, or another appropriate urgent-care service immediately.',
        },
        {
          type: 'p',
          text: 'Our genetic testing provides preventive background information, not a diagnosis of an inherited condition. A clinician may require confirmatory testing before acting on a result.',
        },
        {
          type: 'p',
          text: 'We may recommend further testing or care from a GP, urgent-care service or specialist. You are responsible for acting on urgent advice and follow-up recommendations.',
        },
      ],
    },
    {
      number: 4,
      title: 'Eligibility',
      blocks: [
        { type: 'p', text: 'You may use the Service only if:' },
        {
          type: 'ul',
          items: [
            'you are over 18 years of age',
            'you can enter into a binding contract',
            'you are located in a jurisdiction where Melio offers the Service',
            'you provide the information we reasonably request',
            'you meet the identity and suitability requirements for the Service',
          ],
        },
        { type: 'p', text: 'Melio may decline, pause, or discontinue the Service where:' },
        {
          type: 'ul',
          items: [
            'the Service is not clinically suitable',
            'the information available is incomplete or unreliable',
            "urgent clinical concerns require care outside Melio's scope",
            'you cannot understand or consent to the Service',
            'continuing would create a safety risk',
            'a partner laboratory, clinician, or regulatory requirement prevents continued service delivery',
          ],
        },
      ],
    },
    {
      number: 5,
      title: 'Account And Identity',
      blocks: [
        {
          type: 'p',
          text: 'Keep your account credentials secure and your contact and health information current. Tell us promptly about relevant new results or inaccurate information.',
        },
        {
          type: 'p',
          text: 'Melio may ask you to verify your identity before providing testing coordination, releasing results, or sharing clinically relevant outputs.',
        },
      ],
    },
    {
      number: 6,
      title: 'Your Responsibilities',
      blocks: [
        { type: 'p', text: 'You agree to:' },
        {
          type: 'ul',
          items: [
            'provide accurate and complete information',
            'disclose relevant symptoms, diagnoses, tests, medications and family history',
            'follow testing and sample instructions',
            'act promptly on emergency or urgent-care advice',
            'attend or reschedule consultations',
            'limit information about relatives to what your care requires',
          ],
        },
        { type: 'p', text: 'You must not:' },
        {
          type: 'ul',
          items: [
            'misuse the platform',
            'impersonate another person',
            'upload false or misleading records',
            'use Melio as a substitute for urgent medical help',
          ],
        },
      ],
    },
    {
      number: 7,
      title: 'Clinician Review And Surveillance Outputs',
      blocks: [
        { type: 'p', text: 'A clinician reviews and approves every member-specific finding before it is finalised.' },
        { type: 'p', text: 'Your care plan, recommendations and summaries are based on the information available at the time, including:' },
        {
          type: 'ul',
          items: [
            'information supplied by you',
            'laboratory or pathology partner data',
            'test limitations',
            'any need for confirmatory testing',
          ],
        },
        { type: 'p', text: 'Melio may revise a plan or recommendation if new information becomes available.' },
      ],
    },
    {
      number: 8,
      title: 'Partner Laboratories And Testing Providers',
      blocks: [
        { type: 'p', text: 'External partners provide testing, sample collection and related logistics.' },
        { type: 'p', text: 'Those partners may handle:' },
        {
          type: 'ul',
          items: [
            'kit delivery, sample collection and return',
            'blood analysis by Inuvi Diagnostics Limited',
            'genotyping by AttoDiagnostics Ltd, which returns raw genotype data to Melio',
          ],
        },
        {
          type: 'p',
          text: 'Partners provide analytical results, not clinical interpretation. Melio interprets results and a clinician reviews member-specific findings. Partner availability, sample failures and repeat testing may affect timing.',
        },
        {
          type: 'p',
          text: 'Where clinically appropriate, Melio may recommend repeat testing, confirmatory testing, or referral to a different provider.',
        },
      ],
    },
    {
      number: 9,
      title: 'Fees, Payments, And Refunds',
      blocks: [
        { type: 'p', text: 'Fees for the Service will be shown before purchase unless otherwise agreed in writing.' },
        { type: 'p', text: 'You agree to pay the price and any additional costs shown before purchase.' },
        { type: 'p', text: 'Unless otherwise stated at checkout or in a separate refund policy:' },
        {
          type: 'ul',
          items: [
            'consultation fees may become non-refundable once a clinician has been allocated or preparation has materially begun',
            'testing costs may become non-refundable once a kit is shipped, a requisition is issued or a partner incurs a non-recoverable cost',
            'missed appointments and late cancellations may be charged',
          ],
        },
        { type: 'p', text: 'Melio may suspend access to non-urgent parts of the Service for non-payment.' },
      ],
    },
    {
      number: 10,
      title: 'Rescheduling And Delays',
      blocks: [
        {
          type: 'p',
          text: 'We may reschedule or delay work because of clinician availability, incomplete information, testing or quality issues, or legal and safety requirements. We will notify you of material delays.',
        },
      ],
    },
    {
      number: 11,
      title: 'Complaints And Safety Concerns',
      blocks: [
        { type: 'p', text: 'Send complaints to help@meliotherapeutics.uk. We may refer clinical complaints to a senior clinician.' },
        {
          type: 'p',
          text: 'If you believe there is an urgent safety issue, do not wait for an in-app response. Use appropriate urgent medical channels immediately.',
        },
      ],
    },
    {
      number: 12,
      title: 'Communications',
      blocks: [
        { type: 'p', text: 'You agree that Melio may contact you by email, SMS, phone, secure message, or in-app notification in connection with:' },
        {
          type: 'ul',
          items: [
            'appointments',
            'onboarding',
            'result handling',
            'safety escalation',
            'administrative updates',
            'service-related notices',
          ],
        },
        { type: 'p', text: 'Keep your contact details current and check service communications promptly.' },
      ],
    },
    {
      number: 13,
      title: 'Privacy And Data Protection',
      blocks: [
        {
          type: 'p',
          text: 'We process personal information, including health and genetic information, as described in our Privacy Notice and testing consent materials at [meliotherapeutics.uk/privacy](/privacy). The Privacy Notice explains what we collect, how laboratories and other providers use it, how long we retain it and how to exercise your rights. Raw genetic data is held in a restricted, encrypted and logged store and may be deleted separately from the rest of your record.',
        },
      ],
    },
    {
      number: 14,
      title: 'Intellectual Property',
      blocks: [
        {
          type: 'p',
          text: 'Melio and its licensors own the platform, software, branding and service materials. You retain the rights in your personal information and records provided by law.',
        },
        { type: 'p', text: 'You may use the Service only for your personal, non-commercial use unless we agree otherwise in writing.' },
        {
          type: 'p',
          text: "You must not copy, reverse engineer, republish, or exploit Melio's software, workflows, or materials except as permitted by law.",
        },
      ],
    },
    {
      number: 15,
      title: 'Suspension Or Termination',
      blocks: [
        { type: 'p', text: 'Melio may suspend or terminate your access to all or part of the Service if:' },
        {
          type: 'ul',
          items: [
            'you materially breach these Terms',
            'you misuse the platform',
            'the Service is no longer clinically appropriate',
            'continued access would create safety, legal, or regulatory risk',
            'your account is compromised',
            'a required clinician or partner service is unavailable',
          ],
        },
        {
          type: 'p',
          text: 'Termination will not affect rights or obligations that arose before termination, including payment obligations already incurred.',
        },
      ],
    },
    {
      number: 16,
      title: 'Liability',
      blocks: [
        {
          type: 'p',
          text: 'Nothing in these Terms excludes or limits liability where it would be unlawful to do so, including liability for death or personal injury caused by negligence, fraud, or any other liability that cannot lawfully be excluded.',
        },
        { type: 'p', text: 'Subject to the paragraph above, Melio is not responsible to the extent a loss is caused by:' },
        {
          type: 'ul',
          items: [
            'inaccurate or incomplete information you provide',
            'your failure to seek urgent care when advised or clearly required',
            "a third-party system or provider outside Melio's reasonable control",
          ],
        },
        { type: 'p', text: 'Melio remains responsible for its own obligations as required by law.' },
      ],
    },
    {
      number: 17,
      title: 'Changes To The Service Or These Terms',
      blocks: [
        {
          type: 'p',
          text: 'We may update the Service or these terms for operational, legal, regulatory or product reasons. We will notify you before a material change takes effect.',
        },
      ],
    },
    {
      number: 18,
      title: 'Governing Law',
      blocks: [
        { type: 'p', text: 'These Terms are governed by the laws of England and Wales, unless mandatory local consumer law requires otherwise.' },
        {
          type: 'p',
          text: 'The courts of England and Wales will have exclusive jurisdiction, subject to any mandatory rights you may have under applicable consumer law.',
        },
      ],
    },
    {
      number: 19,
      title: 'Contact Details',
      blocks: [
        {
          type: 'p',
          text: 'Melio Health Solutions Ltd\nRegistered office: 167-169 Great Portland Street, 5th Floor, London W1W 5PF\nCompany number: 16497996\nEmail: help@meliotherapeutics.uk\nWebsite: [meliotherapeutics.uk](https://meliotherapeutics.uk)',
        },
      ],
    },
  ],
  history: [
    {
      version: '1.3',
      date: '11 September 2026',
      change:
        'Removed promotional, repetitive and internal drafting language; simplified service, partner, privacy and liability terms without changing the service perimeter.',
    },
    {
      version: '1.2',
      date: '22 June 2026',
      change:
        'Regulatory review (REP-001) — drop "consumer" genetics framing (§3); absolute clinician sign-off (§7); remove "white-label" Atto language (§8, §13).',
    },
    { version: '1.1', date: '10 June 2026', change: 'Prior issue for review.' },
  ],
};
